// =============================================================================
// MeuDriver — StudentProgress
// Mapa de evolução do aluno: radar de habilidades + linha do tempo — /aluno/progresso
// Batch T4: adicionado ProgressReport imprimível + botão "Imprimir"
// =============================================================================

import { TrendingUp, Clock, BookOpen, ChevronRight, Printer } from 'lucide-react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { SkillRadarChart } from '@/components/student/SkillRadarChart'
import { LessonHistoryTimeline } from '@/components/student/LessonHistoryTimeline'
import { XPProgressBar } from '@/components/student/XPProgressBar'
import { ProgressReport } from '@/components/student/ProgressReport'
import { useProgressReport } from '@/hooks/useProgressReport'
import {
  useStudentSkillAverages,
  useStudentLessonHistory,
  useStudentProgressSummary,
} from '@/hooks/useStudentProgress'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'

const CNH_GOAL_HOURS = 20

export default function StudentProgress() {
  const { data: averages, isLoading: avgLoading } = useStudentSkillAverages()
  const { data: history,  isLoading: histLoading } = useStudentLessonHistory()
  const { data: summary,  isLoading: sumLoading  } = useStudentProgressSummary()
  const reportData = useProgressReport()

  const totalHours   = summary?.totalHours   ?? 0
  const totalLessons = summary?.totalLessons ?? 0
  const progressPct  = Math.min(100, Math.round((totalHours / CNH_GOAL_HOURS) * 100))

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      {/* Componente de impressão — oculto na tela, visível ao print */}
      <ProgressReport data={reportData} />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-brand-600" />
              Meu progresso
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Acompanhe sua evolução nas habilidades de direção.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            className="print:hidden shrink-0"
            data-testid="progress-print-button"
          >
            <Printer className="h-4 w-4 mr-1.5" />
            Imprimir
          </Button>
        </div>
      </motion.div>

      {/* XP e nível */}
      <XPProgressBar />

      {/* Cards de resumo */}
      <div className="grid grid-cols-3 gap-3">
        {sumLoading ? (
          <>
            <Skeleton className="h-20 rounded-2xl" />
            <Skeleton className="h-20 rounded-2xl" />
            <Skeleton className="h-20 rounded-2xl" />
          </>
        ) : (
          <>
            <div className="rounded-2xl bg-white border border-border/60 p-4 text-center shadow-soft">
              <BookOpen className="h-5 w-5 text-brand-500 mx-auto mb-1" />
              <p className="text-2xl font-extrabold text-gray-900">{totalLessons}</p>
              <p className="text-xs text-muted-foreground">Aulas</p>
            </div>
            <div className="rounded-2xl bg-white border border-border/60 p-4 text-center shadow-soft">
              <Clock className="h-5 w-5 text-teal-500 mx-auto mb-1" />
              <p className="text-2xl font-extrabold text-gray-900">{totalHours}h</p>
              <p className="text-xs text-muted-foreground">Horas</p>
            </div>
            <div className="rounded-2xl bg-white border border-border/60 p-4 text-center shadow-soft">
              <TrendingUp className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
              <p className="text-2xl font-extrabold text-gray-900">{progressPct}%</p>
              <p className="text-xs text-muted-foreground">da meta CNH</p>
            </div>
          </>
        )}
      </div>

      {/* Meta CNH */}
      <div className="rounded-2xl border border-border/60 bg-white shadow-soft p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-gray-900">Meta para a CNH</p>
          <span className="text-xs text-muted-foreground">{totalHours}h / {CNH_GOAL_HOURS}h</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="h-full rounded-full bg-gradient-to-r from-brand-500 to-teal-500"
          />
        </div>
        {progressPct >= 100 && (
          <p className="text-xs text-green-600 font-semibold mt-2">
            Parabéns! Você atingiu a meta de horas para a CNH.
          </p>
        )}
      </div>

      {/* Radar de habilidades */}
      <div className="rounded-2xl border border-border/60 bg-white shadow-soft p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-900">Habilidades de direção</h2>
          {averages && averages.lessons_rated > 0 && (
            <span className="text-xs text-muted-foreground">
              {averages.lessons_rated} avaliação{averages.lessons_rated !== 1 ? 'ões' : ''}
            </span>
          )}
        </div>
        <SkillRadarChart averages={averages ?? null} isLoading={avgLoading} />
      </div>

      {/* Linha do tempo */}
      <div className="rounded-2xl border border-border/60 bg-white shadow-soft p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-900">Histórico de aulas</h2>
          {(history?.length ?? 0) > 5 && (
            <Link
              to="/aluno/dashboard"
              className="flex items-center gap-0.5 text-sm text-brand-600 font-semibold hover:text-brand-700 transition-colors"
            >
              Ver todas
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
        <LessonHistoryTimeline
          entries={history ?? []}
          isLoading={histLoading}
          limit={10}
        />
      </div>
    </div>
  )
}
